import React, { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";

export default function OwnTheAgentApplicationPage() {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1: Basic Information
    fullName: "",
    email: "",
    companyName: "",
    industry: "",

    // Step 2: AI & Goals
    businessChallenges: "",
    aiExperience: "",
    sixMonthOutcome: "",

    // Step 3: Investment & Scheduling
    investmentOption: "",
    timeZone: "",
    weeklyTimeCommitment: "",
    referralSource: "",
  });

  const totalSteps = 3;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const nextStep = () => {
    window.scrollTo(0, 0);
    setStep((prevStep) => Math.min(prevStep + 1, totalSteps));
  };

  const prevStep = () => {
    window.scrollTo(0, 0);
    setStep((prevStep) => Math.max(prevStep - 1, 1));
  };

  const submitApplication = async () => {
    // Basic validation (optional, but good practice)
    if (!formData.email || !formData.fullName) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please ensure all required fields are filled out.",
      });
      // Consider adding logic to highlight missing fields or go back to step 1
      return;
    }

    setSubmitting(true);

    // Update this URL to the newly deployed one
    const scriptUrl =
      "https://script.google.com/macros/s/AKfycbxQT-RpHmMDuGEj2v1XA3_DQIH0diSNcbKISWRqZNef7VJi6LKEb2WvQZSeJ_00caTI9w/exec";

    try {
      const params = new URLSearchParams();
      params.append("timestamp", new Date().toISOString());
      // Use email from form data instead of Clerk user
      params.append("email", formData.email);
      params.append("formType", "ota-application"); // Identifier for the script

      // Append all form data fields
      for (const [key, value] of Object.entries(formData)) {
        params.append(key, String(value ?? ""));
      }

      await fetch(`${scriptUrl}?${params.toString()}`, {
        method: "GET",
        mode: "no-cors",
      });

      toast({
        title: "Application Submitted!",
        description: "We've received your application and will be in touch.",
      });
      setStep(totalSteps + 1);
    } catch (error) {
      console.error("Application submission error:", error);
      toast({
        variant: "destructive",
        title: "Submission Error",
        description: "Something went wrong. Please try submitting again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const stepTitles = [
    "Basic Information",
    "AI Experience & Goals",
    "Investment & Scheduling",
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
      <div className="container mx-auto px-4 max-w-3xl">
        <Card className="shadow-lg">
          <CardHeader>
            <div className="flex items-center justify-between mb-2">
              <Badge
                variant="outline"
                className="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-3 py-1"
              >
                Step {step} of {totalSteps}
              </Badge>
              {step <= totalSteps && (
                <div className="text-sm text-muted-foreground">
                  {Math.round((step / totalSteps) * 100)}% complete
                </div>
              )}
            </div>
            <CardTitle className="text-2xl">
              {step <= totalSteps
                ? stepTitles[step - 1]
                : "Application Submitted"}
            </CardTitle>
            <CardDescription>
              {step === 1 &&
                "Let's start with some basic information about you and your business."}
              {step === 2 &&
                "Tell us about your experience with AI and your goals."}
              {step === 3 &&
                "Let us know your investment preferences and availability."}
              {step > totalSteps &&
                "Thank you for your application to the Own the Agent™ program."}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Step 1: Basic Information */}
            {step === 1 && (
              <>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="companyName">Company Name *</Label>
                    <Input
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry *</Label>
                    <Select
                      onValueChange={(value) =>
                        handleInputChange({
                          target: { name: "industry", value },
                        })
                      }
                      value={formData.industry}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select your industry" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="marketing">
                          Marketing/Advertising
                        </SelectItem>
                        <SelectItem value="ecommerce">E-commerce</SelectItem>
                        <SelectItem value="saas">SaaS/Software</SelectItem>
                        <SelectItem value="consulting">Consulting</SelectItem>
                        <SelectItem value="finance">Finance</SelectItem>
                        <SelectItem value="education">Education</SelectItem>
                        <SelectItem value="healthcare">Healthcare</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            {/* Step 2: AI Experience & Goals */}
            {step === 2 && (
              <>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="aiExperience">
                      How would you describe your current experience with AI? *
                    </Label>
                    <RadioGroup
                      value={formData.aiExperience}
                      onValueChange={(value) =>
                        handleInputChange({
                          target: { name: "aiExperience", value },
                        })
                      }
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="beginner" id="exp-beginner" />
                        <Label htmlFor="exp-beginner" className="font-normal">
                          Beginner - Just starting to explore AI
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="intermediate"
                          id="exp-intermediate"
                        />
                        <Label
                          htmlFor="exp-intermediate"
                          className="font-normal"
                        >
                          Intermediate - Using some AI tools regularly
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="advanced" id="exp-advanced" />
                        <Label htmlFor="exp-advanced" className="font-normal">
                          Advanced - Already implementing AI in my business
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="businessChallenges">
                      What are the top business challenges you hope AI could
                      help solve? *
                    </Label>
                    <Textarea
                      id="businessChallenges"
                      name="businessChallenges"
                      value={formData.businessChallenges}
                      onChange={handleInputChange}
                      placeholder="e.g., Time-consuming reporting, Scaling client services, Content creation"
                      rows={4}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sixMonthOutcome">
                      What would a successful outcome from this program look
                      like for you? *
                    </Label>
                    <Textarea
                      id="sixMonthOutcome"
                      name="sixMonthOutcome"
                      value={formData.sixMonthOutcome}
                      onChange={handleInputChange}
                      placeholder="Be as specific as possible about what success would look like"
                      rows={4}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Step 3: Investment & Scheduling */}
            {step === 3 && (
              <>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="investmentOption">
                      Which investment option are you most interested in? *
                    </Label>
                    <RadioGroup
                      value={formData.investmentOption}
                      onValueChange={(value) =>
                        handleInputChange({
                          target: { name: "investmentOption", value },
                        })
                      }
                    >
                      <div className="flex items-start space-x-2 p-4 border rounded-md mb-2 bg-indigo-50 dark:bg-indigo-900/20">
                        <RadioGroupItem
                          value="annual"
                          id="investment-annual"
                          className="mt-1"
                        />
                        <div>
                          <Label
                            htmlFor="investment-annual"
                            className="font-bold block"
                          >
                            Annual Membership (€11,999)
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Full year of implementation support
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2 p-4 border rounded-md mb-2">
                        <RadioGroupItem
                          value="six-month"
                          id="investment-six"
                          className="mt-1"
                        />
                        <div>
                          <Label
                            htmlFor="investment-six"
                            className="font-bold block"
                          >
                            Six-Month Accelerator (€8,999)
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Six months of intensive implementation
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2 p-4 border rounded-md">
                        <RadioGroupItem
                          value="monthly"
                          id="investment-monthly"
                          className="mt-1"
                        />
                        <div>
                          <Label
                            htmlFor="investment-monthly"
                            className="font-bold block"
                          >
                            Monthly Implementation (€2,499/month)
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            3-month minimum commitment
                          </p>
                        </div>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="weeklyTimeCommitment">
                      How much time can you commit to this program weekly? *
                    </Label>
                    <Select
                      onValueChange={(value) =>
                        handleInputChange({
                          target: { name: "weeklyTimeCommitment", value },
                        })
                      }
                      value={formData.weeklyTimeCommitment}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select time commitment" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-2">1-2 hours</SelectItem>
                        <SelectItem value="3-5">3-5 hours</SelectItem>
                        <SelectItem value="6-10">6-10 hours</SelectItem>
                        <SelectItem value="10+">10+ hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timeZone">What is your time zone? *</Label>
                    <Select
                      onValueChange={(value) =>
                        handleInputChange({
                          target: { name: "timeZone", value },
                        })
                      }
                      value={formData.timeZone}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select your time zone" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pst">Pacific Time (PT)</SelectItem>
                        <SelectItem value="mst">Mountain Time (MT)</SelectItem>
                        <SelectItem value="cst">Central Time (CT)</SelectItem>
                        <SelectItem value="est">Eastern Time (ET)</SelectItem>
                        <SelectItem value="gmt">UK (GMT)</SelectItem>
                        <SelectItem value="cet">
                          Central European (CET)
                        </SelectItem>
                        <SelectItem value="aest">
                          Australian Eastern (AEST)
                        </SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            {/* Thank You Step */}
            {step > totalSteps && (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900 mb-6">
                  <Check className="h-8 w-8 text-green-600 dark:text-green-300" />
                </div>
                <h3 className="text-2xl font-bold mb-4">
                  Application Submitted Successfully!
                </h3>
                <p className="text-muted-foreground mb-6">
                  Thank you for your interest in the Own the Agent™ program.
                  We'll review your application and get back to you within 2
                  business days.
                </p>
                <Button asChild className="mt-4">
                  <a href="/">Return to Homepage</a>
                </Button>
              </div>
            )}
          </CardContent>

          {step <= totalSteps && (
            <CardFooter className="flex justify-between border-t p-6">
              {step > 1 ? (
                <Button variant="outline" onClick={prevStep}>
                  <ArrowLeft className="mr-2 h-4 w-4" /> Previous
                </Button>
              ) : (
                <div></div>
              )}

              {step < totalSteps ? (
                <Button onClick={nextStep}>
                  Next <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  onClick={submitApplication}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Application"
                  )}
                </Button>
              )}
            </CardFooter>
          )}
        </Card>
      </div>
    </div>
  );
}
